'use client';

import {ReactNode, useEffect, useRef, useState} from 'react';

type Props = {
    children: ReactNode;
    /** Reserve layout height while hidden to prevent Cumulative Layout Shift (CLS). */
    minHeight?: number;
    className?: string;
};

/**
 * Defers client-side rendering of children until the section is 300px from the viewport.
 *
 * ✅ Use ONLY for below-the-fold, media-only sections:
 *   - Pure image carousels / logo grids (no visible text headings)
 *   - Video galleries / embeds
 *   - Third-party widget containers (cookie banners, chat widgets)
 *
 * ❌ NEVER wrap content containing:
 *   - Headings (h1–h6), body text, paragraphs
 *   - FAQs, testimonial copy, review text
 *   - CTAs, internal links, navigation elements
 *   - JSON-LD / structured data scripts
 *
 * These must be in the server-rendered HTML for Google, LLMs, and AI crawlers to
 * read and cite them without JavaScript execution.
 *
 * For mixed sections (text + images): remove LazySection entirely and use next/image
 * without the priority prop on individual images — they lazy-load by default.
 */
export default function LazySection({children, minHeight, className}: Props) {
    const [isVisible, setIsVisible] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = ref.current;
        if (!el) return undefined;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    // Unobserve immediately — this fires once and never again.
                    observer.unobserve(el);
                }
            },
            // 300px vertical pre-load margin: content starts rendering before it enters
            // the viewport, giving the browser time to fetch images on slow connections.
            // Horizontal margin is 0px to avoid spurious triggers on horizontal layouts.
            {rootMargin: '200px 0px'}
        );

        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    return (
        <div ref={ref} className={className} style={!isVisible && minHeight !== null && minHeight !== undefined ? {minHeight} : undefined}>
            {isVisible ? children : null}
        </div>
    );
}
